(ns manage.resource.adset-preview
  (:require [common.db.effects.http :refer [jsonify]]
            [common.re-frame :as cr]
            [common.url-template :as url-tpl]
            [manage.resource.content-function-version :as content-function-version]
            [re-frame.core :as rf]))

;; Interceptor for adding the map as first argument used for preview handlers:
;; #{:api-url :preview-token :preview-root}
(def preview-interceptor
  (rf/->interceptor
   :id :preview
   :before (fn preview-before [context]
             (let [api-url (:apiUrl @(cr/no-warning-subscribe [:config]))
                   preview-token (:preview-token @(cr/no-warning-subscribe [:context]))
                   [_ & args] (get-in context [:coeffects :event])
                   m {:api-url api-url
                      :preview-token preview-token}
                   event (->> args (cons m) vec)]
               (assoc-in context [:coeffects :event] event)))))

;; Get preview data with two async requests
;; {:general          `/preview`
;;  :content-function `/preview/content-function`}
(rf/reg-event-fx :resource.adset-preview/get-data
  [preview-interceptor]
  (fn [{:keys [db]} [url-context stage]]
    (when-let [content-function-url (url-tpl/fully-render :api/preview-content-function url-context)]
      (when-let [general-url (url-tpl/fully-render :api/preview url-context)]
        {:db (assoc db :resource.adset-preview/data {:state :pending})
         :http {:requests [{:id :general
                            :method :get
                            :uri general-url
                            :params {:embed (jsonify ["template"])}}
                           {:id :content-function
                            :method :get
                            :uri content-function-url
                            :params (when stage (hash-map :stage stage))}]
                :on-success [:resource.adset-preview/get-data-success]
                :on-failure [:resource.adset-preview/get-data-failure]}}))))

(rf/reg-event-db :resource.adset-preview/get-data-success
  [(rf/path :resource.adset-preview/data)]
  (fn [_ [_ data]]
    {:state :success :value data}))

(rf/reg-event-db :resource.adset-preview/get-data-failure
  [(rf/path :resource.adset-preview/data)]
  (fn [_ _]
    {:state :failure}))

(rf/reg-event-fx :resource.adset-preview/get-adset-data
  [preview-interceptor]
  (fn [{:keys [db]} [url-context]]
    {:db (assoc db :resource.adset-preview/adset-data {:state :pending})
     :http {:method :get
            :uri (url-tpl/fully-render :api/preview url-context)
            :params {:embed (jsonify ["template"])}
            :on-success [:resource.adset-preview/get-adset-data-success]
            :on-failure [:resource.adset-preview/get-adset-data-failure]}}))

(rf/reg-event-db :resource.adset-preview/get-adset-data-success
  (fn [db [_ data]]
    (assoc-in db [:resource.adset-preview/adset-data] {:state :success
                                                       :value data})))

(rf/reg-event-db :resource.adset-preview/get-adset-data-failure
  (fn [db _]
    (assoc-in db [:resource.adset-preview/adset-data] {:state :failure
                                                       :value nil})))

;; Get content functions to see if there are staged content functions
(rf/reg-event-fx :resource.adset-preview/get-content-function-overview
  [preview-interceptor]
  (fn [{:keys [db]} [url-context]]
    (when-let [url (url-tpl/fully-render :api/preview-content-function-overview url-context)]
      {:db (assoc db :resource.adset-preview/content-function-overview {:state :pending})
       :http {:method :get
              :uri url
              :on-success [:resource.adset-preview/get-content-function-overview-success]
              :on-failure [:resource.adset-preview/get-content-function-overview-failure]}})))

(rf/reg-event-db :resource.adset-preview/get-content-function-overview-success
  [content-function-version/overview-success-response->versions
   (rf/path :resource.adset-preview/content-function-overview)]
  (fn [_ [_ versions]]
    {:state :success :value versions}))

(rf/reg-event-db :resource.adset-preview/get-content-function-overview-failure
  [(rf/path :resource.adset-preview/content-function-overview)]
  (fn [_ _]
    {:state :failure}))

;; Get your context data
(rf/reg-event-fx :resource.adset-preview/get-your-context
  [preview-interceptor]
  (fn [{:keys [db]} [url-context stage]]
    (when-let [url (url-tpl/fully-render :api/preview-context url-context)]
      {:db (assoc db :resource.adset-preview/your-context {:state :pending})
       :http {:method :get
              :uri url
              :params (when stage (hash-map :stage stage))
              :on-success [:resource.adset-preview/get-your-context-success]
              :on-failure [:resource.adset-preview/get-your-context-failure]}})))

(rf/reg-event-db :resource.adset-preview/get-your-context-success
  [(rf/path :resource.adset-preview/your-context)]
  (fn [_ [_ your-context]]
    {:state :success :value your-context}))

(rf/reg-event-db :resource.adset-preview/get-your-context-failure
  [(rf/path :resource.adset-preview/your-context)]
  (fn [_ _]
    {:state :failure}))

;; Evaluate context with content function
(rf/reg-event-fx :resource.adset-preview/evaluate
  [preview-interceptor]
  (fn [{:keys [db]} [url-context cfn stage]]
    (when-let [url (url-tpl/fully-render :api/preview-eval url-context)]
      (let [{:keys [content-function
                    add-request-context
                    products
                    width
                    height]
             :or {add-request-context false}} cfn
            context (-> (:context cfn)
                        (merge {:creative {:width width :height height}})
                        (merge products))]
        {:db (assoc-in db
                       [:resource.adset-preview/evaluate context]
                       {:state :pending})
         :http {:method :post
                :uri (cond-> url
                       stage (str "?stage=" stage))
                :params {:addRequestContext add-request-context
                         :contentFunction content-function
                         :context context}
                :on-success [:resource.adset-preview/evaluate-success context]
                :on-failure [:resource.adset-preview/evaluate-failure context]}}))))

(rf/reg-event-db :resource.adset-preview/evaluate-success
  [(rf/path :resource.adset-preview/evaluate)]
  (fn [state [_ context content]]
    (assoc-in state [context] {:state :success :value content})))

(rf/reg-event-db :resource.adset-preview/evaluate-failure
  [(rf/path :resource.adset-preview/evaluate)]
  (fn [state [_ context]]
    (assoc-in state [context] {:state :failure})))

(rf/reg-sub :resource.adset-preview/adset-name
  (fn [db]
    (get-in db [:resource.adset-preview/adset-data :value :adsetName])))

(rf/reg-sub :resource.adset-preview/adset-channel
  (fn [db]
    (get-in db [:resource.adset-preview/adset-data :value :channel])))
